/**
 * Carrega dinamicamente o Google Maps JavaScript API
 * com singleton pattern, cache-busting e error handling robusto
 */

let googleMapsLoadPromise: Promise<void> | null = null;

export async function loadGoogleMaps(
  apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyBpkOYi8B_lACfJFdbviamBdttm_I-ZDSQ'
): Promise<void> {
  // Reutilizar promise existente (previne múltiplos carregamentos)
  if (googleMapsLoadPromise) {
    console.log('📦 Reutilizando promise existente do Google Maps');
    return googleMapsLoadPromise;
  }

  // Se já está carregado, retorna imediatamente
  if (typeof window !== 'undefined' && window.google?.maps?.places && window.google?.maps?.geometry?.spherical && window.google?.maps?.drawing) {
    console.log('✅ Google Maps já está carregado');
    return Promise.resolve();
  }

  // Criar e cachear nova promise
  googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
    // Verificar se o script já está sendo carregado
    const existingScript = document.querySelector('script[data-google-maps]');
    if (existingScript) {
      console.log('🔄 Script já está no DOM, aguardando carregar...');
      
      // Aguardar o script existente carregar
      const waitForLoad = () => {
        if (window.google?.maps?.places && window.google?.maps?.geometry?.spherical && window.google?.maps?.drawing) {
          console.log('✅ Google Maps disponível (script pré-existente)');
          resolve();
        } else {
          console.log('⏳ Aguardando Places API, Geometry e Drawing...');
          setTimeout(waitForLoad, 100);
        }
      };
      waitForLoad();
      return;
    }

    console.log('🚀 Iniciando carregamento do Google Maps...');

    // Configurar callback de erro de autenticação
    if (!(window as any).gm_authFailure) {
      (window as any).gm_authFailure = () => {
        console.error('🔐 gm_authFailure disparado!');
        console.error('Verifique: Chave API, Billing, APIs habilitadas, Restrições HTTP');
        reject(new Error('Erro de autenticação do Google Maps (gm_authFailure)'));
      };
    }

    // Criar e injetar o script
    const script = document.createElement('script');
    const timestamp = Date.now();
    
    // A nova API não precisa de callback - Web Components são carregados automaticamente
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry,drawing&region=BR&language=pt-BR&v=weekly&loading=async&ver=${timestamp}`;
    script.async = true;
    script.setAttribute('data-google-maps', 'true');

    // Timeout de segurança
    const timeoutId = setTimeout(() => {
      console.error('⏱️ Timeout de 15s ao carregar Google Maps');
      reject(new Error('Timeout ao carregar Google Maps (15s)'));
    }, 15000);

    // Handler de carregamento bem-sucedido
    script.onload = () => {
      clearTimeout(timeoutId);
      console.log('✅ Script do Google Maps carregado');
      
      // Polling para garantir Places API está disponível
      let attempts = 0;
      const maxAttempts = 50; // 5 segundos
      
      const checkPlaces = () => {
        attempts++;
        if (window.google?.maps?.places && window.google?.maps?.geometry?.spherical && window.google?.maps?.drawing) {
          console.log(`✅ Places API, Geometry e Drawing disponíveis após ${attempts * 100}ms`);
          resolve();
        } else if (attempts < maxAttempts) {
          setTimeout(checkPlaces, 100);
        } else {
          console.error('❌ Places API, Geometry ou Drawing não inicializaram após 5s');
          reject(new Error('Places API, Geometry ou Drawing não inicializaram'));
        }
      };
      
      checkPlaces();
    };

    script.onerror = (error) => {
      clearTimeout(timeoutId);
      console.error('❌ Erro ao baixar script do Google Maps:', error);
      reject(new Error('Falha ao baixar o script do Google Maps'));
    };

    document.head.appendChild(script);
    console.log('📝 Script injetado no DOM');
  });

  // Resetar cache em caso de erro para permitir retry
  googleMapsLoadPromise.catch((err) => {
    console.error('🔄 Erro ao carregar, resetando cache para retry:', err);
    googleMapsLoadPromise = null;
    throw err;
  });

  return googleMapsLoadPromise;
}
